import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError, timer } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';
import { CoreModule } from '../core.module';
import { environment } from '../../../environments/environment';

const RETRYABLE_STATUS_CODES = new Set([0, 502, 503, 504]);
const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000;
type HttpClientPostOptionsParams = Parameters<HttpClient['post']>[2];

@Injectable({
	providedIn: CoreModule,
})
export class ServerConnectionService {
	constructor(private httpc: HttpClient) {
	}

	public post$<T = any>(url: string, data: any, options?: HttpClientPostOptionsParams): Observable<T> {
		return this.httpc.post<T>(url, data, options).pipe(this.withRetry());
	}

	public get$<T = any>(url: string, query = {}): Observable<T> {
		return this.httpc.get<T>(url, { params: query }).pipe(this.withRetry());
	}

	public put$<T = any>(url: string, data: any): Observable<T> {
		return this.httpc.put<T>(url, data).pipe(this.withRetry());
	}

	public delete$<T = any>(url: string): Observable<T> {
		return this.httpc.delete<T>(url).pipe(this.withRetry());
	}

	public http$(method, url, query, data) {
		let _url = url;

		// For Local Staging -- Simple way to run app locally with staging server handling the backend.
		if (environment.urlOverride && _url.includes('http') === false) {
			_url = environment.urlOverride + _url;
		}
		if (method === 'GET') {
			if (query) {
				_url = `${_url}?${query}`;
			}
			return this.httpc.get(_url).pipe(
				this.withRetry(),
				catchError(this.handleError(_url, [])),
			);
		} if (method === 'POST') {
			if (query) {
				_url = `${_url}?${query}`;
			}
			return this.httpc.post(_url, data).pipe(
				this.withRetry(),
				catchError(this.handleError(_url, [])),
			);
		} if (method === 'PUT') {
			if (query) {
				_url = `${_url}?${query}`;
			}
			return this.httpc.put(_url, data).pipe(
				this.withRetry(),
				catchError(this.handleError(_url, [])),
			);
		} if (method === 'DELETE') {
			if (query) {
				_url = `${_url}?${query}`;
			}
			return this.httpc.delete(_url).pipe(
				this.withRetry(),
				catchError(this.handleError(_url, [])),
			);
		}

		return throwError('Invalid HTTP method');
	}

	private withRetry<T>() {
		return retry<T>({
			count: MAX_RETRIES,
			delay: (error: HttpErrorResponse, retryCount: number) => {
				if (error instanceof HttpErrorResponse && RETRYABLE_STATUS_CODES.has(error.status)) {
					return timer(RETRY_DELAY_MS * retryCount);
				}
				throw error;
			},
		});
	}

	private handleError<T>(operation = 'operation', result?: T) {
		return (error: any): Observable<T> => of(result as T);
	}
}
